<script setup lang="ts">
/* =============================================================
   BOOKINGS
   =============================================================
   Every booking the company can see, in one list: the stays it
   booked for its ships and the stays booked at its berths. RLS
   returns exactly those two sets, so there is no per-owner or
   per-berth view to jump between — one operational overview,
   split only by time (upcoming / past) and narrowed by filters.

   Upcoming reads soonest first, because that is the order the
   work arrives in; past reads most recent first.
   ============================================================= */

import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import Alert from '@/components/ui/Alert.vue'
import Button from '@/components/ui/Button.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import FilterInput from '@/components/ui/FilterInput.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import SelectField from '@/components/ui/SelectField.vue'
import Tabs from '@/components/ui/Tabs.vue'
import BookingOverviewList from '@/components/bookings/BookingOverviewList.vue'
import { routes } from '@/config/routes'
import { useAuth } from '@/lib/auth/useAuth'
import { listBookings } from '@/lib/bookings'
import { BOOKING_STATES, bookingState, bookingStateLabel } from '@/lib/bookingLabels'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import { errorMessage } from '@/lib/errors'
import type { Booking } from '@/lib/types'

useDocumentTitle('Bookings')
const { user } = useAuth()

const bookings = ref<Booking[]>([])
const loading = ref(true)
const error = ref('')

onMounted(async () => {
  try {
    bookings.value = await listBookings()
  } catch (e) {
    error.value = errorMessage(e, 'Could not load bookings.')
  } finally {
    loading.value = false
  }
})

/* --- filters ------------------------------------------------------------ */

type Side = 'all' | 'mine' | 'berths'

const route = useRoute()
const period = ref<string>('upcoming')
/* `?status=requested` is how the dashboard links to "waiting for you". */
const initialStatus = String(route.query.status ?? '')
const status = ref((BOOKING_STATES as string[]).includes(initialStatus) ? initialStatus : 'all')
const side = ref<Side>('all')
const query = ref('')

const companyId = computed(() => user.value?.companyId)
const isMine = (b: Booking) => b.company_id === companyId.value
const atMyBerth = (b: Booking) => b.berth?.company_id === companyId.value

/** The side filter only means something when both sides are in the list. */
const hasBothSides = computed(
  () => bookings.value.some(isMine) && bookings.value.some(atMyBerth),
)

/**
 * Upcoming = still going to happen: requested or confirmed, not yet ended.
 * Everything else -- finished, cancelled, rejected -- is history.
 */
const isPast = (b: Booking) =>
  b.status === 'cancelled' || b.status === 'rejected' || new Date(b.departure).getTime() < Date.now()

const matching = computed(() => {
  const q = query.value.trim().toLowerCase()
  return bookings.value.filter((b) => {
    if (status.value !== 'all' && bookingState(b) !== status.value) return false
    if (side.value === 'mine' && !isMine(b)) return false
    if (side.value === 'berths' && !atMyBerth(b)) return false
    if (!q) return true
    return [b.reference, b.berth?.name, b.berth?.city, b.vessel?.name, b.company?.name]
      .join(' ')
      .toLowerCase()
      .includes(q)
  })
})

const upcoming = computed(() =>
  matching.value
    .filter((b) => !isPast(b))
    .sort((a, b) => a.arrival.localeCompare(b.arrival)),
)
const past = computed(() => matching.value.filter(isPast))

/* Picking "Cancelled" while on Upcoming would show nothing; go where the
   matches are instead. */
watch(status, () => {
  if (shown.value.length > 0) return
  if (period.value === 'upcoming' && past.value.length > 0) period.value = 'past'
  else if (period.value === 'past' && upcoming.value.length > 0) period.value = 'upcoming'
})

const tabs = computed(() => [
  { key: 'upcoming', label: 'Upcoming', count: upcoming.value.length },
  { key: 'past', label: 'Past & cancelled', count: past.value.length },
])

const shown = computed(() => (period.value === 'upcoming' ? upcoming.value : past.value))

const awaiting = computed(
  () => bookings.value.filter((b) => b.status === 'requested' && atMyBerth(b) && !isPast(b)).length,
)

const filtered = computed(
  () => status.value !== 'all' || side.value !== 'all' || query.value.trim() !== '',
)

function clearFilters(): void {
  status.value = 'all'
  side.value = 'all'
  query.value = ''
}

const description = computed(() => {
  const role = user.value?.role
  if (role === 'shipowner') return 'Every stay booked for your fleet.'
  if (role === 'infrastructure_owner') return 'Every stay booked at your berths.'
  return 'Stays booked for your fleet and at your berths, in one place.'
})
</script>

<template>
  <PageHeader title="Bookings" :description="description">
    <template v-if="user?.role !== 'infrastructure_owner'" #actions>
      <Button icon="search" :to="routes.search">Find a berth</Button>
    </template>
  </PageHeader>

  <Alert v-if="error" tone="danger" class="page-alert">{{ error }}</Alert>

  <p v-else-if="loading" class="page-status">Loading…</p>

  <Panel v-else-if="bookings.length === 0">
    <EmptyState
      icon="bookings"
      title="No bookings yet"
      :description="
        user?.role === 'infrastructure_owner'
          ? 'Bookings at your berths will appear here as they come in.'
          : 'Search for a berth, pick a slot and book it — it will appear here.'
      "
    >
      <template v-if="user?.role !== 'infrastructure_owner'" #action>
        <Button icon="search" :to="routes.search">Find a berth</Button>
      </template>
    </EmptyState>
  </Panel>

  <template v-else>
    <Alert v-if="awaiting > 0" tone="warning" class="page-alert">
      {{ awaiting }} {{ awaiting === 1 ? 'request is' : 'requests are' }} waiting for your answer.
      <button type="button" class="linkButton" @click="((status = 'requested'), (period = 'upcoming'))">
        Show {{ awaiting === 1 ? 'it' : 'them' }}
      </button>
    </Alert>

    <Panel flush>
      <div class="toolbar">
        <div class="filters">
          <SelectField v-model="status" label="Status" class="filter">
            <option value="all">All statuses</option>
            <option v-for="s in BOOKING_STATES" :key="s" :value="s">{{ bookingStateLabel(s) }}</option>
          </SelectField>
          <SelectField v-if="hasBothSides" v-model="side" label="Side" class="filter">
            <option value="all">Both sides</option>
            <option value="mine">Booked by us</option>
            <option value="berths">At our berths</option>
          </SelectField>
          <FilterInput
            v-model="query"
            label="Filter by reference, berth, vessel or company"
            placeholder="Reference, berth, vessel…"
            class="search"
          />
        </div>
      </div>
      <Tabs v-model="period" :tabs="tabs" label="Upcoming or past bookings" class="periodTabs" />

      <div :id="`tabpanel-${period}`" role="tabpanel" :aria-labelledby="`tab-${period}`">
        <div v-if="shown.length === 0" class="panel-status">
          <template v-if="filtered">
            No {{ period === 'past' ? 'past' : 'upcoming' }} bookings match these filters.
            <button type="button" class="linkButton" @click="clearFilters">Clear filters</button>
          </template>
          <template v-else>No {{ period === 'past' ? 'past' : 'upcoming' }} bookings.</template>
        </div>
        <BookingOverviewList v-else :bookings="shown" />
      </div>
    </Panel>
  </template>
</template>

<style scoped>
.toolbar {
  padding: var(--space-sm) var(--space-md) var(--space-2xs);
}

/* The tab bar's own rule is the line between the controls and the list. */
.periodTabs {
  margin-bottom: 0 !important;
  padding-inline: var(--space-sm);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2xs);
}

.filter {
  min-width: 10rem;
}

/* The options say what each select is ("All statuses", "Both sides"), so the
   label is for screen readers only -- a visible one would sit above the
   select and knock it out of line with the search box beside it. */
.filter :deep(.label) {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.search {
  flex: 1 1 14rem;
  max-width: 24rem;
}

/* On a phone the controls stack full width; a row of three would overflow. */
@media (max-width: 40rem) {
  .toolbar {
    padding: var(--space-xs) var(--space-sm) var(--space-3xs);
  }

  .periodTabs {
    padding-inline: var(--space-3xs);
  }

  .filters,
  .filter,
  .search {
    width: 100%;
  }
}

.linkButton {
  padding: 0;
  font: inherit;
  font-weight: var(--weight-semibold);
  color: var(--color-accent);
  text-decoration: underline;
  background: none;
  border: 0;
  cursor: pointer;
}
</style>
